import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Calendar, DollarSign, Package, FileText, Sparkles, Vote, Sun, Moon, Music, Users, PieChart, Download } from 'lucide-react';

/**
 * GuidedTour - Post-onboarding walkthrough
 * 
 * Now navigates to each page as the user progresses through the tour!
 */

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: "You're all set!",
    description: "Let's take a quick tour to show you how to get the most out of Boply.",
    icon: Sparkles,
    color: 'purple',
    page: 'dashboard',
  },
  {
    id: 'calendar',
    title: "Your Calendar",
    description: "Add and manage all your shows, practices, and events. Tap any date to add a new event or view existing ones.",
    icon: Calendar,
    color: 'blue',
    page: 'calendar',
    tip: "💡 Tip: Mark events as tentative, confirmed, or completed — and use 'Other' for practices, meetings, photoshoots, and more.",
  },
  {
    id: 'timeline',
    title: "Timeline View",
    description: "See all your shows in a list view. Past shows show your earnings, upcoming shows show what's next.",
    icon: FileText,
    color: 'indigo',
    page: 'timeline',
    tip: "💡 Tip: Tap a show to see all details and log earnings, payouts, and other info after the gig.",
  },
  {
    id: 'inventory',
    title: "Merch Inventory",
    description: "Track all your merch — t-shirts, vinyl, stickers, whatever you sell. Log sales at shows to keep counts accurate.",
    icon: Package,
    color: 'amber',
    page: 'inventory',
    tip: "💡 Tip: Use Sales Mode at gigs! Log sales in real-time — your inventory updates automatically and all sales are saved to that show's details for easy tracking.",
  },
  {
    id: 'summary',
    title: "Financial Summary",
    description: "See the big picture — total revenue, expenses, and profit across all your shows. Track what's working and what's not.",
    icon: PieChart,
    color: 'green',
    page: 'summary',
    tip: "💡 Tip: Export everything to CSV! Download your shows, expenses, and inventory data anytime for accounting or tax purposes.",
    hasVisual: 'export',
  },
  {
    id: 'polls',
    title: "Band Polls",
    description: "Need to make a group decision? Create a poll! Vote on setlists, merch designs, tour dates, or anything else.",
    icon: Vote,
    color: 'pink',
    page: 'polls',
    tip: "💡 Tip: Polls show a badge when you have unvoted items.",
  },
  {
    id: 'contacts',
    title: "Contacts",
    description: "Keep all your venue contacts, promoters, and industry connections in one place. Link them to shows for easy reference.",
    icon: Users,
    color: 'teal',
    page: 'contacts',
    tip: "💡 Tip: Add contacts from shows automatically when you enter venue info.",
  },
  {
    id: 'settings-tip',
    title: "Settings & More",
    description: "In the ⚙️ Settings menu you can: toggle dark/light mode, import from Bandsintown, manage band members, and send feedback.",
    icon: Sun,
    color: 'yellow',
    page: 'dashboard',
    tip: "💡 Find Settings in the top right corner of the screen.",
  },
  {
    id: 'done',
    title: "You’re all set!",
    description: "That's the basics — the more you log, the better insights you'll get. Now get back to creating!",
    icon: Sparkles,
    color: 'purple',
    page: 'dashboard',
    cta: "Let's Go!",
  },
];

const GuidedTour = ({ isOpen, onClose, onNavigate, currentPage }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset step when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      // Navigate to first step's page
      if (onNavigate && TOUR_STEPS[0].page) {
        onNavigate(TOUR_STEPS[0].page);
      }
    }
  }, [isOpen, onNavigate]);

  // Navigate to the page for the current step
  useEffect(() => {
    if (isOpen && onNavigate && TOUR_STEPS[currentStep]?.page) {
      onNavigate(TOUR_STEPS[currentStep].page);
    }
  }, [currentStep, isOpen, onNavigate]);

  if (!isOpen) return null;

  const step = TOUR_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('boply_tour_completed', 'true');
    onClose();
    if (onNavigate) {
      onNavigate('dashboard');
    }
  };

  const handleSkip = () => {
    localStorage.setItem('boply_tour_skipped', 'true');
    onClose();
    if (onNavigate) {
      onNavigate('dashboard');
    }
  };

  const goToStep = (index) => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentStep(index);
      setIsAnimating(false);
    }, 150);
  };

  // Color schemes
  const colors = {
    purple: {
      bg: 'bg-purple-500/20',
      icon: 'bg-purple-500/30 text-purple-400',
      progress: 'bg-purple-500',
      border: 'border-purple-500/30',
    },
    blue: {
      bg: 'bg-blue-500/20',
      icon: 'bg-blue-500/30 text-blue-400',
      progress: 'bg-blue-500',
      border: 'border-blue-500/30',
    },
    indigo: {
      bg: 'bg-indigo-500/20',
      icon: 'bg-indigo-500/30 text-indigo-400',
      progress: 'bg-indigo-500',
      border: 'border-indigo-500/30',
    },
    green: {
      bg: 'bg-green-500/20',
      icon: 'bg-green-500/30 text-green-400',
      progress: 'bg-green-500',
      border: 'border-green-500/30',
    },
    amber: {
      bg: 'bg-amber-500/20',
      icon: 'bg-amber-500/30 text-amber-400',
      progress: 'bg-amber-500',
      border: 'border-amber-500/30',
    },
    pink: {
      bg: 'bg-pink-500/20',
      icon: 'bg-pink-500/30 text-pink-400',
      progress: 'bg-pink-500',
      border: 'border-pink-500/30',
    },
    yellow: {
      bg: 'bg-yellow-500/20',
      icon: 'bg-yellow-500/30 text-yellow-400',
      progress: 'bg-yellow-500',
      border: 'border-yellow-500/30',
    },
    teal: {
      bg: 'bg-teal-500/20',
      icon: 'bg-teal-500/30 text-teal-400',
      progress: 'bg-teal-500',
      border: 'border-teal-500/30',
    },
  };

  const colorScheme = colors[step.color] || colors.purple;

  // Render different icons for settings step
  const renderIcon = () => {
    if (step.id === 'settings-tip') {
      return (
        <div className="relative">
          <Sun size={28} className="text-yellow-400" />
          <Moon size={16} className="text-indigo-400 absolute -bottom-1 -right-1" />
        </div>
      );
    }
    return <Icon size={32} />;
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 pointer-events-none">
      {/* Semi-transparent backdrop - allows seeing the page behind */}
      <div 
        className="absolute inset-0 bg-black/40 pointer-events-auto"
        onClick={handleSkip}
      />

      {/* Modal - positioned at bottom on mobile, center on desktop */}
      <div 
        className={`
          relative w-full max-w-md bg-gray-900/95 backdrop-blur-lg rounded-2xl shadow-2xl 
          border ${colorScheme.border} overflow-hidden pointer-events-auto
          transform transition-all duration-300
          ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}
          mb-20 sm:mb-0
        `}
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-800">
          <div 
            className={`h-full ${colorScheme.progress} transition-all duration-300`}
            style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-300 transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Step counter & current page indicator */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {currentStep + 1} / {TOUR_STEPS.length}
          </span>
          {step.page && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${colorScheme.bg} ${colorScheme.icon.split(' ')[1]}`}>
              {step.page}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-6 pt-10">
          {/* Icon */}
          <div className={`w-14 h-14 rounded-2xl ${colorScheme.bg} ${colorScheme.icon} flex items-center justify-center mx-auto mb-4`}>
            {renderIcon()}
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-white text-center mb-2">
            {step.title}
          </h2>

          {/* Description */}
          <p className="text-gray-400 text-center text-sm mb-4 leading-relaxed">
            {step.description}
          </p>

          {/* Tip */}
          {step.tip && (
            <div className={`${colorScheme.bg} rounded-xl p-3 mb-4 border ${colorScheme.border}`}>
              <p className="text-xs text-gray-300">
                {step.tip}
              </p>
            </div>
          )}

          {/* Export Visual for Summary step */}
          {step.hasVisual === 'export' && (
            <div className="bg-gray-800/50 rounded-xl p-4 mb-4 border border-gray-700/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white text-sm font-medium">Export Options</span>
                <Download size={16} className="text-green-400" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg">
                  <Calendar size={14} className="text-blue-400" />
                  <span className="text-gray-300 text-xs">Shows & Events</span>
                  <span className="text-gray-500 text-xs ml-auto">.csv</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg">
                  <DollarSign size={14} className="text-green-400" />
                  <span className="text-gray-300 text-xs">Expenses</span>
                  <span className="text-gray-500 text-xs ml-auto">.csv</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg">
                  <Package size={14} className="text-amber-400" />
                  <span className="text-gray-300 text-xs">Inventory</span>
                  <span className="text-gray-500 text-xs ml-auto">.csv</span>
                </div>
              </div>
            </div>
          )}

          {/* Step indicator dots */}
          <div className="flex justify-center gap-1.5 mb-4 flex-wrap">
            {TOUR_STEPS.map((s, index) => (
              <button
                key={index}
                onClick={() => goToStep(index)}
                className={`
                  h-2 rounded-full transition-all duration-300
                  ${index === currentStep 
                    ? `${colorScheme.progress} w-5` 
                    : index < currentStep 
                      ? 'bg-gray-600 w-2 hover:bg-gray-500' 
                      : 'bg-gray-700 w-2 hover:bg-gray-600'
                  }
                `}
                title={s.title}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                className="flex-1 py-2.5 px-4 bg-gray-800 text-gray-300 rounded-xl font-medium 
                         hover:bg-gray-700 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <ChevronLeft size={16} />
                Back
              </button>
            )}
            
            <button
              onClick={handleNext}
              className={`
                flex-1 py-2.5 px-4 rounded-xl font-semibold transition-all text-sm
                flex items-center justify-center gap-2
                ${isLastStep 
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500' 
                  : 'bg-white text-gray-900 hover:bg-gray-100'
                }
              `}
            >
              {isLastStep ? (step.cta || "Let's Go!") : 'Next'}
              {!isLastStep && <ChevronRight size={16} />}
            </button>
          </div>

          {/* Skip link */}
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="w-full mt-3 text-xs text-gray-500 hover:text-gray-400 transition-colors"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;